import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowRight, Sun, Salad, Cherry, Wine, Moon, Leaf, Fish, Sparkles, Bell } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { useState } from 'react';

// Day moments data
const dayMoments = [
  {
    id: 'colazione',
    time: 'Colazione',
    icon: Sun,
    title: 'La mattina inizia qui',
    description: 'Caffetteria selezionata, proposte leggere, atmosfera lenta. Il sole entra dolce, il tempo si dilata.',
    image: 'https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?w=800&q=85',
  },
  {
    id: 'pranzo',
    time: 'Pranzo',
    icon: Salad,
    title: 'Leggerezza mediterranea',
    description: 'Poke bowl fresche, ingredienti di qualità, piatti estivi. Nutrirsi bene, senza appesantirsi.',
    image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&q=85',
  },
  {
    id: 'merenda',
    time: 'Merenda',
    icon: Cherry,
    title: 'Dolcezza pomeridiana',
    description: 'Frutta fresca, gelati artigianali, proposte leggere. Una pausa che rigenera.',
    image: 'https://images.unsplash.com/photo-1488900128323-21503983a07e?w=800&q=85',
  },
  {
    id: 'aperitivo',
    time: 'Aperitivo',
    icon: Wine,
    title: 'L\'ora dorata',
    description: 'Cocktail signature, vini selezionati, piccoli abbinamenti gourmet. Il tramonto ha un sapore.',
    image: 'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=800&q=85',
  },
  {
    id: 'sera',
    time: 'Verso Sera',
    icon: Moon,
    title: 'Prima della notte',
    description: 'Il drink che anticipa la sera. L\'energia cambia, la luce si abbassa, il ritmo si trasforma.',
    image: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=800&q=85',
  },
];

// Poke ingredients
const pokeFeatures = [
  { icon: Fish, title: 'Ingredienti Premium', desc: 'Pesce fresco, verdure di stagione, basi selezionate' },
  { icon: Sparkles, title: 'Personalizzazione', desc: 'Componi la tua bowl secondo i tuoi gusti' },
  { icon: Leaf, title: 'Opzioni per tutti', desc: 'Versioni vegetariane, vegane e proteiche' },
];

export default function FoodDrink() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleDeliveryNotify = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Inserisci la tua email');
      return;
    }
    setSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Ti avviseremo al lancio del delivery!');
    setEmail('');
    setSubmitting(false);
  };

  return (
    <>
      <Helmet>
        <title>Food & Drink | La Villa - Castelfranco Emilia</title>
        <meta name="description" content="Colazione, pranzo, merenda, aperitivo alla Villa. Poke gourmet, cocktail signature, esperienza food premium a Modena." />
        <meta name="keywords" content="ristorante piscina Modena, poke Castelfranco Emilia, aperitivo Modena, colazione piscina, brunch Bologna" />
        <meta property="og:title" content="Food & Drink | La Villa" />
        <meta property="og:description" content="Dalla mattina al tramonto. Un'esperienza culinaria mediterranea." />
      </Helmet>

      <div className="bg-ivory min-h-screen" data-testid="food-drink-page">
        
        {/* HERO */}
        <section ref={heroRef} className="relative h-[70vh] flex items-center justify-center overflow-hidden">
          <motion.div className="absolute inset-0" style={{ y: heroY }}>
            <img
              src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1600&q=85"
              alt="Food & Drink La Villa"
              className="w-full h-full object-cover scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-ivory/20 via-transparent to-ivory" />
          </motion.div>
          
          <motion.div 
            className="relative z-10 text-center px-6 max-w-3xl mx-auto"
            style={{ opacity: heroOpacity }}
          >
            <motion.p
              className="font-body text-xs uppercase tracking-[0.4em] text-champagne mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              L'Esperienza
            </motion.p>
            <motion.h1
              className="font-heading text-5xl md:text-7xl text-night tracking-tight mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Food & Drink
            </motion.h1>
            <motion.p
              className="font-accent text-xl md:text-2xl italic text-night/80 mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              Dalla mattina al tramonto.
            </motion.p>
            <motion.p
              className="font-body text-sm text-stone-600 mb-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              Alla Villa si resta.<br />E si vive.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <Button
                className="bg-night text-ivory hover:bg-stone-800 rounded-full px-8 py-6 text-sm uppercase tracking-widest"
                onClick={() => document.getElementById('giornata')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-experience-btn"
              >
                Scopri l'esperienza
              </Button>
              <Button
                variant="outline"
                className="border-night text-night hover:bg-night hover:text-ivory rounded-full px-8 py-6 text-sm uppercase tracking-widest"
                asChild
              >
                <Link to="/contatti">
                  Prenota un tavolo
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </section>

        {/* SEZIONE 1 — LA GIORNATA */}
        <section id="giornata" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-ivory">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center mb-20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">
                Il Ritmo
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-night tracking-tight">
                Un ritmo che cambia con la luce.
              </h2>
            </motion.div>

            <div className="space-y-24">
              {dayMoments.map((moment, index) => (
                <motion.div
                  key={moment.id}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  data-testid={`moment-${moment.id}`}
                >
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={moment.image}
                        alt={moment.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  </div>
                  
                  <div className={`${index % 2 === 1 ? 'lg:order-1 lg:text-right' : ''}`}>
                    <div className={`flex items-center gap-3 mb-4 ${index % 2 === 1 ? 'lg:justify-end' : ''}`}>
                      <moment.icon className="text-champagne" size={24} strokeWidth={1.5} />
                      <span className="font-body text-xs uppercase tracking-[0.2em] text-champagne">
                        {moment.time}
                      </span>
                    </div>
                    <h3 className="font-heading text-3xl text-night mb-4">
                      {moment.title}
                    </h3>
                    <p className="font-body text-stone-600 leading-relaxed">
                      {moment.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SEZIONE 2 — IL POKE BAR */}
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-stone-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">
                  Il Poke Bar
                </p>
                <h2 className="font-heading text-4xl md:text-5xl text-night tracking-tight mb-6">
                  Poke Selection
                </h2>
                <p className="font-body text-stone-600 leading-relaxed mb-8">
                  Ingredienti freschi, combinazioni bilanciate, estetica curata.<br />
                  Un'esperienza leggera, pensata per l'estate.
                </p>
                
                <div className="space-y-6 mb-10">
                  {pokeFeatures.map((feature, featureIdx) => (
                    <motion.div
                      key={feature.title}
                      className="flex items-start gap-4"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: featureIdx * 0.1 }}
                    >
                      <div className="w-10 h-10 bg-champagne/10 flex items-center justify-center flex-shrink-0">
                        <feature.icon className="text-champagne" size={20} />
                      </div>
                      <div>
                        <h4 className="font-body font-semibold text-night mb-1">{feature.title}</h4>
                        <p className="font-body text-sm text-stone-500">{feature.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <Button
                  variant="outline"
                  className="border-night text-night hover:bg-night hover:text-ivory rounded-full px-8 py-6 text-sm uppercase tracking-widest"
                  data-testid="poke-menu-btn"
                  onClick={() => toast.info('Coming soon...', { description: 'Il menu sarà disponibile a breve!' })}
                >
                  Scopri il menu <ArrowRight className="ml-2" size={16} />
                </Button>
              </motion.div>

              <motion.div
                className="relative"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&q=85"
                    alt="Poke Bowl La Villa"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative element */}
                <div className="absolute -bottom-6 -left-6 w-32 h-32 border border-champagne/30" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* SEZIONE 3 — DELIVERY (TBA) */}
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-ivory">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {/* TBA Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-champagne/10 border border-champagne/30 mb-8">
                <Bell className="text-champagne" size={16} />
                <span className="font-body text-xs uppercase tracking-widest text-champagne">
                  Coming Soon
                </span>
              </div>

              <p className="font-body text-xs uppercase tracking-[0.3em] text-stone-400 mb-4">
                Prossimamente
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-night tracking-tight mb-6">
                Poke Delivery
              </h2>
              <p className="font-body text-stone-600 leading-relaxed mb-4">
                Presto disponibile anche fuori dalla Villa.
              </p>
              <p className="font-accent text-lg italic text-champagne mb-10">
                Stessa qualità. Stessa firma.
              </p>

              <form onSubmit={handleDeliveryNotify} className="max-w-md mx-auto">
                <div className="flex gap-3">
                  <Input
                    type="email"
                    placeholder="La tua email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-white border-stone-200 rounded-full px-6 py-6 text-sm focus:border-champagne"
                    data-testid="delivery-email"
                  />
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-night text-ivory hover:bg-stone-800 rounded-full px-6 py-6 text-sm uppercase tracking-widest"
                    data-testid="delivery-notify-btn"
                  >
                    {submitting ? '...' : 'Avvisami'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </section>

        {/* SEZIONE 4 — FILOSOFIA */}
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-stone-900">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-6">
                La Filosofia
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-ivory tracking-tight mb-10">
                Non è solo cibo.
              </h2>
              <div className="space-y-4 mb-12">
                <p className="font-body text-xl text-stone-400">
                  È tempo condiviso.
                </p>
                <p className="font-body text-xl text-stone-400">
                  È luce che cambia.
                </p>
                <p className="font-body text-xl text-stone-400">
                  È una pausa che diventa momento.
                </p>
              </div>
              <p className="font-accent text-2xl italic text-champagne">
                "Ogni gesto, una firma."
              </p>
            </motion.div>
          </div>
        </section>

        {/* Bottom spacing */}
        <div className="h-20 lg:hidden" />
      </div>
    </>
  );
}
